import React from "react";

// 2026-08-14 (Epic 10) — Guest Intelligence AI Sentence Layer. Promoted to
// `components/shared/` 2026-08-21 (Voice Intelligence, plan D-3) because Voice
// and Chat Intelligence are the second and third consumers; it is generic.
//
// Renders a plain-language takeaway sentence above a panel's chart/table. The
// sentence comes from the { [panel_key]: sentence } map (guest_intel_sentences
// for CDP, conversation_sentences for Voice/Chat), fetched as a flat map.
// Missing sentences are normal (custom date ranges, job hasn't run yet) —
// return null so the chart still renders.
//
// Rules (per the Guest Intelligence spec):
//   - Render verbatim. Never edit, reformat, truncate, or clamp.
//   - Missing sentence = return null (progressive enhancement).
//   - source ('ai'|'template') is backend-only — never style differently.
//   - The Verdict variant is the one exception: when absent, the PARENT
//     hides the entire block. This component just returns null; the parent
//     (VerdictBlock) handles the show/hide logic.
//
// Typography (Epic 6): sizing/weight comes from the .gi-verdict /
// .gi-takeaway classes in index.css (the 6-token scale). Color + spacing
// stay here so the component stays decoupled from any chart and can later
// render alone (voice/glasses) with the chart hidden.

// `sentence` (V6, additive): Search Console carries each panel's sentence on the
// panel object in the page envelope, not in a separate `{ [panelKey]: sentence }`
// map. When given, it takes precedence over the `say[panelKey]` lookup; the map
// path is untouched, so CDP and the voice/chat bands that pass `say` keep working.
//
// `className` (V6, additive): overrides the panel variant's default bottom margin
// (`mb-3`) for callers that need tighter spacing. The money strip's stat cards want
// `mb-2` — this prop is what let the old forked `PanelTakeawayLite` be deleted
// rather than kept as a second implementation of this same render (§D.1#2 / §I).
// Absent, the default `mb-3` is byte-for-byte what every CDP caller already gets.
const PanelTakeaway = ({ panelKey, variant = "panel", say, sentence, className }) => {
  const text = sentence ?? say?.[panelKey];
  if (!text) return null;

  if (variant === "verdict") {
    return (
      <p className="gi-verdict text-gray-900 dark:text-gray-100">
        {text}
      </p>
    );
  }

  return (
    <p className={`gi-takeaway text-gray-700 dark:text-gray-300 ${className ?? "mb-3"}`}>
      {text}
    </p>
  );
};

export default PanelTakeaway;
